import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import { db, isConfigured } from '../firebaseConfig';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';

export default function Dashboard() {
  const { currentUser } = useAuth();
  const [stats, setStats] = useState({
    totalProjects: 0,
    completedProjects: 0,
    inProgressProjects: 0,
    totalClients: 0,
    totalSkills: 0,
    unreadMessages: 0,
    pendingReviews: 0
  });
  const [recentProjects, setRecentProjects] = useState([]);
  const [recentMessages, setRecentMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isConfigured) {
      fetchAllData();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchAllData = async () => {
    try {
      await Promise.all([
        fetchStats(),
        fetchRecentProjects(),
        fetchRecentMessages()
      ]);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      // Total projects
      const projectsSnapshot = await getDocs(collection(db, 'projects'));
      
      // Completed projects
      const completedQuery = query(
        collection(db, 'projects'),
        where('status', '==', 'Completed')
      );
      const completedSnapshot = await getDocs(completedQuery);
      
      // In progress projects
      const inProgressQuery = query(
        collection(db, 'projects'),
        where('status', '==', 'In Progress')
      );
      const inProgressSnapshot = await getDocs(inProgressQuery);
      
      // Total clients
      const clientsSnapshot = await getDocs(collection(db, 'clients'));
      
      // Unread messages
      const unreadQuery = query(
        collection(db, 'messages'),
        where('status', '==', 'unread')
      );
      const unreadSnapshot = await getDocs(unreadQuery);
      
      // Pending reviews
      const pendingReviewsQuery = query(
        collection(db, 'reviews'),
        where('approved', '==', false)
      );
      const pendingReviewsSnapshot = await getDocs(pendingReviewsQuery);
      
      // Tech stack count
      const profileDoc = await getDocs(collection(db, 'profile'));
      let techStackCount = 0;
      profileDoc.forEach(doc => {
        if (doc.data().techStack) {
          techStackCount = doc.data().techStack.length;
        }
      });

      setStats({
        totalProjects: projectsSnapshot.size,
        completedProjects: completedSnapshot.size,
        inProgressProjects: inProgressSnapshot.size,
        totalClients: clientsSnapshot.size,
        totalSkills: techStackCount,
        unreadMessages: unreadSnapshot.size,
        pendingReviews: pendingReviewsSnapshot.size
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchRecentProjects = async () => {
    try {
      const q = query(
        collection(db, 'projects'),
        orderBy('createdAt', 'desc'),
        limit(5)
      );
      const snapshot = await getDocs(q);
      const projects = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRecentProjects(projects);
    } catch (error) {
      console.error('Error fetching recent projects:', error);
    }
  };

  const fetchRecentMessages = async () => {
    try {
      const q = query(
        collection(db, 'messages'),
        orderBy('createdAt', 'desc'),
        limit(3)
      );
      const snapshot = await getDocs(q);
      const messages = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRecentMessages(messages);
    } catch (error) {
      console.error('Error fetching recent messages:', error);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return new Date(timestamp).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  if (loading) {
    return (
      <div className='flex min-h-screen bg-[#FFB7CE] items-center justify-center'>
        <div className='text-white text-xl'>Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className='flex min-h-screen bg-[#FFB7CE]'>
      <Sidebar />
      <div className='flex-1 p-4 pt-20 lg:pt-4 lg:pl-8'>
        <div className='max-w-7xl mx-auto'>
          {/* Header */}
          <div className='mb-8'>
            <h1 className='text-4xl font-bold text-white mb-2'>DASHBOARD</h1>
            <p className='text-white text-lg'>
              {getGreeting()}, {currentUser?.email?.split('@')[0] || 'Admin'}! 👋
            </p>
          </div>

          {/* Stats Grid */}
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8'>
            {/* Total Projects */}
            <div className='bg-white rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow'>
              <div className='flex items-center justify-between mb-4'>
                <div>
                  <p className='text-gray-600 text-sm font-medium'>Total Projects</p>
                  <p className='text-3xl font-bold text-[#FFB7CE]'>{stats.totalProjects}</p>
                </div>
                <div className='bg-[#FFB7CE] bg-opacity-20 rounded-full p-3'>
                  <svg className='w-8 h-8 text-[#FFB7CE]' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z' />
                  </svg>
                </div>
              </div>
              <a href='/projects' className='text-[#FFB7CE] hover:underline text-sm font-medium'>
                View all →
              </a>
            </div>

            {/* Total Skills */}
            <div className='bg-white rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow'>
              <div className='flex items-center justify-between mb-4'>
                <div>
                  <p className='text-gray-600 text-sm font-medium'>Total Skills</p>
                  <p className='text-3xl font-bold text-[#89CFF0]'>{stats.totalSkills}</p>
                </div>
                <div className='bg-[#89CFF0] bg-opacity-20 rounded-full p-3'>
                  <svg className='w-8 h-8 text-[#89CFF0]' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z' />
                  </svg>
                </div>
              </div>
              <a href='/profile' className='text-[#89CFF0] hover:underline text-sm font-medium'>
                Manage skills →
              </a>
            </div>

            {/* Completed Projects */}
            <div className='bg-white rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow'>
              <div className='flex items-center justify-between mb-4'>
                <div>
                  <p className='text-gray-600 text-sm font-medium'>Completed Projects</p>
                  <p className='text-3xl font-bold text-green-500'>{stats.completedProjects}</p>
                </div>
                <div className='bg-green-100 rounded-full p-3'>
                  <svg className='w-8 h-8 text-green-500' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
                  </svg>
                </div>
              </div>
              <p className='text-sm text-gray-500'>
                {stats.inProgressProjects} in progress
              </p>
            </div>

            {/* Total Clients */}
            <div className='bg-white rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow'>
              <div className='flex items-center justify-between mb-4'>
                <div>
                  <p className='text-gray-600 text-sm font-medium'>Total Clients</p>
                  <p className='text-3xl font-bold text-purple-500'>{stats.totalClients}</p>
                </div>
                <div className='bg-purple-100 rounded-full p-3'>
                  <svg className='w-8 h-8 text-purple-500' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' />
                  </svg>
                </div>
              </div>
              <a href='/clients' className='text-purple-500 hover:underline text-sm font-medium'>
                View all →
              </a>
            </div>
          </div>

          {/* Action Cards */}
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8'>
            {/* Notifications */}
            <div className='bg-white rounded-lg p-6 shadow-lg'>
              <h2 className='text-xl font-bold text-gray-800 mb-4 flex items-center gap-2'>
                <svg className='w-6 h-6 text-[#FFB7CE]' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9' />
                </svg>
                Notifications
              </h2>
              
              <div className='space-y-3'>
                {stats.unreadMessages > 0 && (
                  <a href='/messages' className='flex items-center justify-between p-3 bg-red-50 rounded-lg hover:bg-red-100 transition-colors'>
                    <div className='flex items-center gap-3'>
                      <div className='bg-red-500 rounded-full w-2 h-2'></div>
                      <div>
                        <p className='font-medium text-gray-800'>{stats.unreadMessages} Unread Messages</p>
                        <p className='text-sm text-gray-600'>New contact form submissions</p>
                      </div>
                    </div>
                    <svg className='w-5 h-5 text-gray-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
                    </svg>
                  </a>
                )}

                {stats.pendingReviews > 0 && (
                  <a href='/reviews' className='flex items-center justify-between p-3 bg-yellow-50 rounded-lg hover:bg-yellow-100 transition-colors'>
                    <div className='flex items-center gap-3'>
                      <div className='bg-yellow-500 rounded-full w-2 h-2'></div>
                      <div>
                        <p className='font-medium text-gray-800'>{stats.pendingReviews} Pending Reviews</p>
                        <p className='text-sm text-gray-600'>Waiting for approval</p>
                      </div>
                    </div>
                    <svg className='w-5 h-5 text-gray-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
                    </svg>
                  </a>
                )}

                {stats.inProgressProjects > 0 && (
                  <a href='/projects' className='flex items-center justify-between p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors'>
                    <div className='flex items-center gap-3'>
                      <div className='bg-blue-500 rounded-full w-2 h-2'></div>
                      <div>
                        <p className='font-medium text-gray-800'>{stats.inProgressProjects} Projects In Progress</p>
                        <p className='text-sm text-gray-600'>Currently working on</p>
                      </div>
                    </div>
                    <svg className='w-5 h-5 text-gray-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
                    </svg>
                  </a>
                )}

                {stats.unreadMessages === 0 && stats.pendingReviews === 0 && stats.inProgressProjects === 0 && (
                  <div className='text-center py-8 text-gray-500'>
                    <svg className='w-16 h-16 mx-auto mb-4 text-gray-300' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
                    </svg>
                    <p>All caught up! No new notifications.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className='bg-white rounded-lg p-6 shadow-lg'>
              <h2 className='text-xl font-bold text-gray-800 mb-4'>Quick Actions</h2>
              <div className='grid grid-cols-2 gap-3'>
                <a href='/projects' className='flex flex-col items-center justify-center p-4 bg-[#FFB7CE] bg-opacity-10 hover:bg-opacity-20 rounded-lg transition-all duration-200 group'>
                  <svg className='w-8 h-8 text-[#FFB7CE] mb-2 group-hover:scale-110 transition-transform' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 4v16m8-8H4' />
                  </svg>
                  <span className='text-sm font-medium text-gray-700'>Add Project</span>
                </a>

                <a href='/clients' className='flex flex-col items-center justify-center p-4 bg-[#89CFF0] bg-opacity-10 hover:bg-opacity-20 rounded-lg transition-all duration-200 group'>
                  <svg className='w-8 h-8 text-[#89CFF0] mb-2 group-hover:scale-110 transition-transform' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z' />
                  </svg>
                  <span className='text-sm font-medium text-gray-700'>Add Client</span>
                </a>

                <a href='/services' className='flex flex-col items-center justify-center p-4 bg-purple-100 hover:bg-purple-200 rounded-lg transition-all duration-200 group'>
                  <svg className='w-8 h-8 text-purple-500 mb-2 group-hover:scale-110 transition-transform' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' />
                  </svg>
                  <span className='text-sm font-medium text-gray-700'>Add Service</span>
                </a>

                <a href='/profile' className='flex flex-col items-center justify-center p-4 bg-green-100 hover:bg-green-200 rounded-lg transition-all duration-200 group'>
                  <svg className='w-8 h-8 text-green-500 mb-2 group-hover:scale-110 transition-transform' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' />
                  </svg>
                  <span className='text-sm font-medium text-gray-700'>Edit Profile</span>
                </a>
              </div>
            </div>
          </div>

          {/* Recent Projects */}
          <div className='bg-white rounded-lg p-6 shadow-lg mb-8'>
            <div className='flex justify-between items-center mb-4'>
              <h2 className='text-xl font-bold text-gray-800'>Recent Projects</h2>
              <a href='/projects' className='text-[#FFB7CE] hover:underline text-sm font-medium'>
                View all →
              </a>
            </div>

            {recentProjects.length === 0 ? (
              <div className='text-center py-8 text-gray-500'>
                <p>No projects yet. Start by adding your first project!</p>
              </div>
            ) : (
              <div className='overflow-x-auto'>
                <table className='w-full'>
                  <thead className='bg-gray-50'>
                    <tr>
                      <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase'>Tag</th>
                      <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase'>Name</th>
                      <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase'>Client</th>
                      <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase'>Status</th>
                      <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase'>Date</th>
                    </tr>
                  </thead>
                  <tbody className='divide-y divide-gray-200'>
                    {recentProjects.map((project) => (
                      <tr key={project.id} className='hover:bg-gray-50'>
                        <td className='px-4 py-3'>
                          <span className='bg-[#89CFF0] text-white px-2 py-1 rounded-full text-xs'>
                            {project.tag}
                          </span>
                        </td>
                        <td className='px-4 py-3 font-medium text-gray-900'>{project.title}</td>
                        <td className='px-4 py-3 text-sm text-gray-600'>{project.clientName || 'Personal'}</td>
                        <td className='px-4 py-3'>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            project.status === 'Completed' ? 'bg-green-100 text-green-800' :
                            project.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {project.status}
                          </span>
                        </td>
                        <td className='px-4 py-3 text-sm text-gray-500'>{formatDate(project.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Recent Messages */}
          {recentMessages.length > 0 && (
            <div className='bg-white rounded-lg p-6 shadow-lg'>
              <div className='flex justify-between items-center mb-4'>
                <h2 className='text-xl font-bold text-gray-800'>Recent Messages</h2>
                <a href='/messages' className='text-[#FFB7CE] hover:underline text-sm font-medium'>
                  View all →
                </a>
              </div>

              <div className='space-y-3'>
                {recentMessages.map((message) => (
                  <div key={message.id} className='flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors'>
                    <div className='bg-[#FFB7CE] rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0'>
                      <span className='text-white font-bold'>
                        {message.fullName?.charAt(0) || message.firstName?.charAt(0) || '?'}
                      </span>
                    </div>
                    <div className='flex-1 min-w-0'>
                      <div className='flex items-center justify-between mb-1'>
                        <p className='font-medium text-gray-900'>{message.fullName || `${message.firstName} ${message.lastName}`}</p>
                        {message.status === 'unread' && (
                          <span className='bg-red-500 text-white px-2 py-1 rounded-full text-xs'>New</span>
                        )}
                      </div>
                      <p className='text-sm text-gray-600 truncate'>{message.message}</p>
                      <p className='text-xs text-gray-400 mt-1'>{formatDate(message.createdAt)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}